/**
 * `<ContactStatusBadge>` — active / disabled / deleted pill.
 *
 * Per NFR-A11Y-2: every status badge carries an `aria-label` so screen
 * readers announce the status, not just the visible chip color.
 */

import { useTranslation } from "react-i18next";

import { StatusPill, type PillTone } from "../status-pill";
import type { Contact } from "../../lib/api/types";

export type ContactStatus = "active" | "disabled" | "deleted";

export function deriveStatus(contact: Pick<Contact, "isDisabled" | "deletedAt">): ContactStatus {
  if (contact.deletedAt) return "deleted";
  if (contact.isDisabled) return "disabled";
  return "active";
}

export interface ContactStatusBadgeProps {
  contact: Pick<Contact, "isDisabled" | "deletedAt">;
}

const ARIA_LABELS: Record<
  ContactStatus,
  "statusAria.active" | "statusAria.disabled" | "statusAria.deleted"
> = {
  active: "statusAria.active",
  disabled: "statusAria.disabled",
  deleted: "statusAria.deleted",
};

const STATUS_LABELS: Record<ContactStatus, "status.active" | "status.disabled" | "status.deleted"> =
  {
    active: "status.active",
    disabled: "status.disabled",
    deleted: "status.deleted",
  };

const STATUS_TONE: Record<ContactStatus, PillTone> = {
  active: "success",
  deleted: "danger",
  disabled: "neutral",
};

export function ContactStatusBadge({ contact }: ContactStatusBadgeProps) {
  const { t } = useTranslation("contacts");
  const status = deriveStatus(contact);
  return (
    <StatusPill
      tone={STATUS_TONE[status]}
      aria-label={t(ARIA_LABELS[status])}
      data-testid={`contact-status-${status}`}
    >
      {t(STATUS_LABELS[status])}
    </StatusPill>
  );
}
